import { useEffect, useRef, useState } from 'react';
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MUNICIPIOS_REGIAO } from '../../utils/municipios';
import { MAP_STYLES, MAP_SETTINGS, validateMunicipios } from '../../utils/mapConfig';
import { FULL_MAP_GEOJSON, MUNICIPIOS_BOUNDS, REGIAO_BOUNDS } from '../../data/municipiosGeoJSON';
import { getEventIconUrl } from '../../utils/categoryIcons';
import { analytics } from '../../services/analyticsTracker';

// Helper to calculate target zoom level for bounds
const getBoundsZoomLevel = (bounds, mapDim) => {
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;

  const latRad = (lat) => {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  };

  const zoom = (mapDp, worldDp, fraction) => {
    return Math.floor(Math.log(mapDp / worldDp / fraction) / Math.LN2);
  };

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

  const lngDiff = ne.lng() - sw.lng();
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
};


const MapWithEvents = ({ 
  events: propEvents, 
  selectedEvent, 
  onEventSelect, 
  filterMunicipio, 
  filterCategory, 
  setOutOfBoundsIndicators,
  mapType = 'satellite',
  resetViewTrigger = 0,
  onZoomChange,
  onInitialZoomSet
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const cityLabelsRef = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(MAP_SETTINGS.zoom);
  const [isMapCentering, setIsMapCentering] = useState(false);
  const [mapError, setMapError] = useState(null);
  const boundsTimeoutRef = useRef(null); 
  const markerLibRef = useRef(null);

  const onZoomChangeRef = useRef(onZoomChange);
  const onInitialZoomSetRef = useRef(onInitialZoomSet);

  useEffect(() => {
    onZoomChangeRef.current = onZoomChange;
    onInitialZoomSetRef.current = onInitialZoomSet;
  });

  // We directly use propEvents instead of maintaining local state
  const events = propEvents;

  // Initialize Map
  useEffect(() => {
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!GOOGLE_MAPS_API_KEY) {
      console.error("Google Maps API Key is missing!");
      return;
    }

    // Initialize the loader
    setOptions({
      key: GOOGLE_MAPS_API_KEY,
      v: "weekly",
      libraries: ["places"]
    });

    // Load the map
    importLibrary("maps").then(({ Map }) => {
      if (!mapRef.current || mapInstanceRef.current) return;

      // Create map using immutable settings and styles
      const map = new Map(mapRef.current, {
        ...MAP_SETTINGS,
        mapTypeId: mapType,
        styles: mapType === 'roadmap' ? MAP_STYLES : [] // Only apply custom dark styles to roadmap
      });

      mapInstanceRef.current = map;
      setMapLoaded(true);

      // Adiciona a malha territorial e máscara de foco aos dados do mapa
      try {
        map.data.addGeoJson(FULL_MAP_GEOJSON);
      } catch (err) {
        console.error("Erro ao carregar malha GeoJSON no mapa:", err);
      }

      // Listener de zoom para re-clusterização com debounce e telemetria
      let zoomTimeoutId;
      map.addListener('zoom_changed', () => {
        clearTimeout(zoomTimeoutId);
        zoomTimeoutId = setTimeout(() => {
          if (mapInstanceRef.current) {
            const z = mapInstanceRef.current.getZoom();
            setCurrentZoom(z);
            if (onZoomChangeRef.current) {
              onZoomChangeRef.current(z);
            }
            analytics.trackMapNavigation('zoom', { zoom_level: z });
          }
        }, 150);
      });

      // Listener de arrasto espacial (pan)
      map.addListener('dragend', () => {
        if (mapInstanceRef.current) {
          const center = mapInstanceRef.current.getCenter();
          analytics.trackMapNavigation('pan', {
            lat: center ? center.lat() : null,
            lng: center ? center.lng() : null
          });
        }
      });

      // Load AdvancedMarkerElement for city labels
      importLibrary("marker").then(({ AdvancedMarkerElement }) => {
        if (!mapInstanceRef.current) return;

        // Limpar labels antigos se houver
        cityLabelsRef.current.forEach(label => label.map = null);
        cityLabelsRef.current = [];

        // Validar integridade antes de renderizar
        if (!validateMunicipios()) {
          console.warn("Renderização de municípios abortada devido a falha de integridade.");
          return;
        }

        // Criar labels customizados para os 13 municípios da região
        MUNICIPIOS_REGIAO.forEach(city => {
          // Criar elemento HTML para o rótulo
          const labelDiv = document.createElement('div');
          labelDiv.className = 'city-custom-label';
          labelDiv.textContent = city.nome;
          
          // Estilizar inline para garantir funcionamento independentemente do CSS global
          Object.assign(labelDiv.style, {
            color: '#cbd5e1',
            fontSize: '12px',
            fontWeight: '600',
            textShadow: '-1px -1px 0 #0f172a, 1px -1px 0 #0f172a, -1px 1px 0 #0f172a, 1px 1px 0 #0f172a',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            transform: 'translate(-50%, -50%)',
            opacity: '1',
            transition: 'opacity 0.3s ease'
          });

          const labelMarker = new AdvancedMarkerElement({
            map: mapInstanceRef.current,
            position: { lat: city.lat, lng: city.lng },
            content: labelDiv,
            zIndex: 1, // Fica abaixo dos eventos
          });

          cityLabelsRef.current.push({
            marker: labelMarker,
            element: labelDiv
          });
        });

        // Adicionar listener de zoom para controlar a visibilidade
        map.addListener('zoom_changed', () => {
          const currentZoom = map.getZoom();
          // Lógica de renderização condicional:
          // Se o zoom for maior que 11 (muito perto), esconde as cidades para focar nos eventos
          // Se for menor ou igual a 11, mostra os nomes das cidades para contexto regional
          const isVisible = currentZoom <= 11;
          
          cityLabelsRef.current.forEach(labelObj => {
            if (labelObj.element) {
              labelObj.element.style.opacity = isVisible ? '1' : '0';
            }
          });
        });

        // Trigger inicial
        window.google.maps.event.trigger(map, 'zoom_changed');

      }).catch(e => console.error("Error loading AdvancedMarkerElement", e));

    }).catch(e => {
      console.error("Error loading Google Maps", e);
    });

  }, [mapType]); // Adicionada dependência mapType para resolver aviso do ESLint

  // Handle Map Type change
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) return;
    
    const map = mapInstanceRef.current;
    map.setMapTypeId(mapType);
    
    // Update styles based on map type
    if (mapType === 'roadmap') {
      map.setOptions({ styles: MAP_STYLES });
    } else {
      map.setOptions({ styles: [] });
    }
  }, [mapType, mapLoaded]);

  // Dynamic styling for municipality boundaries, Piripiri highlight, and outer dimming mask
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) return;
    const map = mapInstanceRef.current;

    const hasFilter = Boolean(filterMunicipio && filterMunicipio.toLowerCase() !== 'todos');
    const filterNorm = hasFilter ? filterMunicipio.trim().toLowerCase() : null;

    map.data.setStyle((feature) => {
      const isMask = feature.getProperty('isMask');

      // 1. Máscara Externa: Esmaece suavemente as áreas fora dos 13 municípios de interesse
      if (isMask) {
        return {
          fillColor: '#020617', // Slate 950 bem escuro
          fillOpacity: mapType === 'satellite' ? 0.38 : 0.52,
          strokeWeight: 0,
          clickable: false,
          zIndex: 1
        };
      }

      // 2. Delimitação dos Municípios
      const name = feature.getProperty('name');
      const isPiripiri = feature.getProperty('isPiripiri');
      const isSelected = hasFilter && name && name.toLowerCase() === filterNorm;

      if (isSelected) {
        // Destaque Forte do Município Filtrado (Linha nítida, brilhante e destacada)
        return {
          strokeColor: '#38bdf8', // Sky 400 vibrante
          strokeWeight: 3.5,
          strokeOpacity: 1.0,
          fillColor: '#0ea5e9', // Sky 500
          fillOpacity: 0.22,
          zIndex: 100,
          clickable: true
        };
      }

      if (hasFilter) {
        // Quando outro município está selecionado no filtro, atenua os demais
        return {
          strokeColor: isPiripiri ? '#0284c7' : '#334155',
          strokeWeight: isPiripiri ? 1.8 : 1.0,
          strokeOpacity: isPiripiri ? 0.55 : 0.35,
          fillColor: '#0f172a',
          fillOpacity: 0.02,
          zIndex: 10,
          clickable: true
        };
      }

      // Estado Padrão (Sem filtro / "Todos os Municípios")
      if (isPiripiri) {
        // Piripiri sempre bem visível e delimitado
        return {
          strokeColor: '#38bdf8', // Destaque característico de Piripiri
          strokeWeight: 2.5,
          strokeOpacity: 0.95,
          fillColor: '#0284c7',
          fillOpacity: 0.12,
          zIndex: 40,
          clickable: true
        };
      }

      // Demais 12 municípios mapeados de interesse
      return {
        strokeColor: '#06b6d4', // Cyan 500
        strokeWeight: 1.5,
        strokeOpacity: 0.75,
        fillColor: '#06b6d4',
        fillOpacity: 0.04,
        zIndex: 20,
        clickable: true
      };
    });

    // Atualizar labels dos municípios com destaque visual se estiver selecionado
    cityLabelsRef.current.forEach(labelObj => {
      if (labelObj.element) {
        const cityName = labelObj.element.textContent;
        const isSelected = hasFilter && cityName && cityName.toLowerCase() === filterNorm;
        if (isSelected) {
          labelObj.element.style.color = '#38bdf8';
          labelObj.element.style.fontSize = '14px';
          labelObj.element.style.fontWeight = '800';
          labelObj.element.style.textShadow = '0 0 12px rgba(56, 189, 248, 0.9), -1px -1px 0 #020617, 1px -1px 0 #020617, -1px 1px 0 #020617, 1px 1px 0 #020617';
        } else {
          labelObj.element.style.color = '#cbd5e1';
          labelObj.element.style.fontSize = '12px';
          labelObj.element.style.fontWeight = '600';
          labelObj.element.style.textShadow = '-1px -1px 0 #0f172a, 1px -1px 0 #0f172a, -1px 1px 0 #0f172a, 1px 1px 0 #0f172a';
        }
      }
    });

  }, [filterMunicipio, mapType, mapLoaded]);

  // Helper: distância geográfica em km (fórmula de Haversine)
  const haversineDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Raio da Terra em km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Helper function for clustering
  const getClusters = (events, zoom, selectedEventId) => {
    const MAX_CLUSTER_ZOOM = 16; // A partir deste zoom, não agrupa mais
    
    // Se o zoom for muito alto, retorna cada evento como seu próprio cluster
    if (zoom >= MAX_CLUSTER_ZOOM) {
      return events.map(event => ({
        lat: parseFloat(event.lat),
        lng: parseFloat(event.lng),
        events: [event]
      }));
    }

    const clusterRadius = 60; // Raio em pixels para agrupar
    // Distância geográfica máxima (km) para agrupar dois pontos no mesmo cluster.
    // Isso impede que um ponto isolado e distante seja "engolido" pelo centro
    // recalculado de um cluster próximo, causando o bug de zoom-out ao clicar.
    const MAX_GEO_DISTANCE_KM = 15;
    const clusters = [];
    const processedEvents = new Set();

    // Helper to convert LatLng to screen-like coordinates for distance calculation
    const project = (lat, lng, zoom) => {
      const siny = Math.sin((lat * Math.PI) / 180);
      const y = Math.min(Math.max(siny, -0.9999), 0.9999);
      return {
        x: (128 + (lng * 256) / 360) * Math.pow(2, zoom),
        y: (128 - (0.5 * Math.log((1 + y) / (1 - y)) * 256) / (2 * Math.PI)) * Math.pow(2, zoom)
      };
    };

    // Primeiro, processar o evento selecionado para garantir que ele tenha seu próprio marcador
    if (selectedEventId) {
      const selectedEvent = events.find(e => e.id === selectedEventId);
      if (selectedEvent) {
        clusters.push({
          lat: parseFloat(selectedEvent.lat),
          lng: parseFloat(selectedEvent.lng),
          events: [selectedEvent]
        });
        processedEvents.add(selectedEvent.id);
      }
    }

    events.forEach(event => {
      if (processedEvents.has(event.id)) return;

      const eventLat = parseFloat(event.lat);
      const eventLng = parseFloat(event.lng);
      const pos = project(eventLat, eventLng, zoom);
      let foundCluster = false;

      for (const cluster of clusters) {
        // Não agrupar com o cluster do evento selecionado se quisermos destaque total
        if (selectedEventId && cluster.events.some(e => e.id === selectedEventId)) continue;

        const clusterPos = project(cluster.lat, cluster.lng, zoom);
        const pixelDist = Math.sqrt(Math.pow(pos.x - clusterPos.x, 2) + Math.pow(pos.y - clusterPos.y, 2));

        if (pixelDist < clusterRadius) {
          // Verificação adicional: o novo ponto não pode estar a mais de
          // MAX_GEO_DISTANCE_KM de NENHUM ponto já existente no cluster.
          // Isso evita o "efeito corrente" onde o centro médio se desloca
          // e puxa pontos cada vez mais distantes.
          const isTooFarFromAnyMember = cluster.events.some(existingEvent => {
            const existLat = parseFloat(existingEvent.lat);
            const existLng = parseFloat(existingEvent.lng);
            return haversineDistance(eventLat, eventLng, existLat, existLng) > MAX_GEO_DISTANCE_KM;
          });

          if (isTooFarFromAnyMember) {
            // Ponto muito distante geograficamente de algum membro — não agrupar
            continue;
          }

          cluster.events.push(event);
          processedEvents.add(event.id);
          // Update cluster center (simple average)
          cluster.lat = cluster.events.reduce((sum, e) => sum + parseFloat(e.lat), 0) / cluster.events.length;
          cluster.lng = cluster.events.reduce((sum, e) => sum + parseFloat(e.lng), 0) / cluster.events.length;
          foundCluster = true;
          break;
        }
      }

      if (!foundCluster) {
        clusters.push({
          lat: eventLat,
          lng: eventLng,
          events: [event]
        });
        processedEvents.add(event.id);
      }
    });

    return clusters;
  };

  // Update markers when events change OR selection changes OR zoom changes
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) return;

    let isMounted = true;

    const addMarkers = async () => {
      let AdvancedMarkerElement = markerLibRef.current;
      if (!AdvancedMarkerElement) {
        try {
          const lib = await importLibrary("marker");
          AdvancedMarkerElement = lib.AdvancedMarkerElement;
          markerLibRef.current = AdvancedMarkerElement;
        } catch (e) {
          console.error("Error loading marker library", e);
          return;
        }
      }

      if (!isMounted) return;

      // Clear existing markers
      markersRef.current.forEach(marker => {
        if (marker.map) marker.map = null;
      });
      markersRef.current = [];

      const clusters = getClusters(events, currentZoom, selectedEvent?.id);

      clusters.forEach(cluster => {
        // Se houver apenas um evento no cluster, renderiza o marcador normal
        if (cluster.events.length === 1) {
          const event = cluster.events[0];
          const iconUrl = getEventIconUrl(event);
          
          const isSelected = selectedEvent && selectedEvent.id === event.id;

          const container = document.createElement('div');
          // Add data-id to easily find and update it later
          container.dataset.eventId = event.id;
          container.className = `event-marker-container ${isSelected ? 'event-marker-selected' : 'event-marker-pulse'}`;
          
          const img = document.createElement('img');
          img.src = iconUrl;
          img.className = `event-marker-icon ${mapType === 'roadmap' ? 'event-marker-icon-roadmap' : ''}`;
          img.alt = event.nome;
          
          container.appendChild(img);

          // Aplicar um pequeno jitter (desvio) se houver eventos com coordenadas idênticas no mesmo local
          // Isso permite ver ícones sobrepostos quando o zoom está no máximo
          let jitterLat = 0;
          let jitterLng = 0;
          
          // Se estamos em zoom alto, aplica um espalhamento em círculo para pontos idênticos
          if (currentZoom >= 16) {
            const index = events.findIndex(e => e.id === event.id);
            // Espalhamento em espiral/círculo baseado no índice
            jitterLat = Math.sin(index * 2) * 0.0001;
            jitterLng = Math.cos(index * 2) * 0.0001;
          }

          const marker = new AdvancedMarkerElement({
            position: { lat: cluster.lat + jitterLat, lng: cluster.lng + jitterLng },
            map: mapInstanceRef.current,
            content: container,
            title: event.nome,
            zIndex: isSelected ? 1000 : 10,
          });

          marker.addListener('click', () => {
            if (onEventSelect) onEventSelect(event);
          });

          markersRef.current.push(marker);
        } else {
          // Renderiza o marcador de cluster (grupo)
          const clusterContainer = document.createElement('div');
          clusterContainer.className = 'event-cluster-container';
          
          const clusterInner = document.createElement('div');
          clusterInner.className = `event-cluster-inner ${mapType === 'roadmap' ? 'event-cluster-inner-roadmap' : ''}`;
          clusterInner.textContent = cluster.events.length;
          
          clusterContainer.appendChild(clusterInner);

          const marker = new AdvancedMarkerElement({
            position: { lat: cluster.lat, lng: cluster.lng },
            map: mapInstanceRef.current,
            content: clusterContainer,
            title: `${cluster.events.length} eventos nesta área`,
            zIndex: 5,
          });

          // Ao clicar no cluster, ajusta a visualização para os eventos daquele grupo
          marker.addListener('click', async () => {
            const map = mapInstanceRef.current;
            const currentZoom = map.getZoom();
            const { LatLngBounds } = await importLibrary("core");
            const bounds = new LatLngBounds();
            
            cluster.events.forEach(e => {
              bounds.extend({ lat: parseFloat(e.lat), lng: parseFloat(e.lng) });
            });
            
            // Verificar se todos os pontos no cluster são idênticos
            const firstEvent = cluster.events[0];
            const isSinglePoint = cluster.events.every(e => 
              parseFloat(e.lat) === parseFloat(firstEvent.lat) && 
              parseFloat(e.lng) === parseFloat(firstEvent.lng)
            );

            if (onEventSelect && firstEvent) {
              onEventSelect(firstEvent);
            }

            if (isSinglePoint) {
              map.panTo({ lat: parseFloat(firstEvent.lat), lng: parseFloat(firstEvent.lng) });
              map.setZoom(17);
            } else {
              const mapDiv = mapRef.current;
              const mapDim = {
                width: mapDiv ? mapDiv.offsetWidth : 400,
                height: mapDiv ? mapDiv.offsetHeight : 400
              };
              
              // Estima o zoom que o fitBounds escolheria (subtraímos 1 para compensar a margem de segurança do Google Maps)
              const estimatedZoom = getBoundsZoomLevel(bounds, mapDim) - 1;
              
              if (estimatedZoom <= currentZoom) {
                // Se o zoom estimado para conter todos os pontos for menor ou igual ao atual
                // (por causa de algum ponto isolado que encadeou no agrupamento),
                // nós forçamos o zoom a aumentar progressivamente para quebrar o cluster.
                const targetZoom = Math.min(currentZoom + 2, 17);
                map.panTo({ lat: cluster.lat, lng: cluster.lng });
                map.setZoom(targetZoom);
              } else {
                map.fitBounds(bounds);
                
                // Evita zoom excessivo em pontos extremamente próximos
                const listener = map.addListener('idle', () => {
                  if (map.getZoom() > 17) map.setZoom(17);
                  window.google.maps.event.removeListener(listener);
                });
              }
            }
          });

          markersRef.current.push(marker);
        }
      });
    };

    addMarkers();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, mapLoaded, currentZoom, mapType]);

  // Update visual selection without recreating all markers
  useEffect(() => {
    if (!mapLoaded) return;
    
    document.querySelectorAll('.event-marker-container').forEach(el => {
      if (selectedEvent && el.dataset.eventId === selectedEvent.id.toString()) {
        el.classList.remove('event-marker-pulse');
        el.classList.add('event-marker-selected');
        // Bring to front
        if (el.parentElement) el.parentElement.style.zIndex = "1000";
      } else {
        el.classList.remove('event-marker-selected');
        el.classList.add('event-marker-pulse');
        if (el.parentElement) el.parentElement.style.zIndex = "10";
      }
    });
  }, [selectedEvent, mapLoaded]);

  // Handle event selection (Pan and Zoom)
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded || !selectedEvent) return;

    if (selectedEvent.lat && selectedEvent.lng) {
      const position = { lat: parseFloat(selectedEvent.lat), lng: parseFloat(selectedEvent.lng) };
      mapInstanceRef.current.panTo(position);
      
      // Smooth zoom if needed (using a safer approach to avoid loops)
      const map = mapInstanceRef.current;
      const currentZoom = map.getZoom();
      
      if (currentZoom < 15) {
        // Use a slight delay to allow panTo to start
        const zoomTimeout = setTimeout(() => {
          map.setZoom(15);
        }, 300);
        return () => clearTimeout(zoomTimeout);
      }
    }
  }, [selectedEvent, mapLoaded]);

  // Handle Map Bounds based on Filters
  const eventIdsString = events.map(e => e.id).sort().join(',');

  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) return;
    // Se há um evento selecionado, mantém a câmera focada nele e não sobrepõe com a visão geral
    if (selectedEvent) return;

    const adjustMapBounds = async () => {
      const map = mapInstanceRef.current;
      setMapError(null);
      setIsMapCentering(true);
      
      try {
        const { LatLngBounds } = await importLibrary("core");

        // 1. Prioridade: Município Selecionado no Filtro (Enquadramento no polígono oficial)
        if (filterMunicipio && filterMunicipio.toLowerCase() !== 'todos') {
          const filterNorm = filterMunicipio.trim().toLowerCase();
          const cityEntry = Object.entries(MUNICIPIOS_BOUNDS).find(
            ([k]) => k.toLowerCase() === filterNorm
          );

          if (cityEntry && cityEntry[1]) {
            const cityData = cityEntry[1];
            const bounds = new LatLngBounds(
              { lat: cityData.south, lng: cityData.west },
              { lat: cityData.north, lng: cityData.east }
            );

            // Ajusta a câmera com padding proporcional
            const isMobile = window.innerWidth < 768;
            const pad = isMobile ? 35 : 60;
            map.fitBounds(bounds, {
              top: pad,
              right: pad,
              bottom: pad,
              left: pad
            });

            setTimeout(() => setIsMapCentering(false), 450);
          } else {
            // Fallback para coordenadas manuais se necessário
            const municipioData = MUNICIPIOS_REGIAO.find(m => m.nome.toLowerCase() === filterNorm);
            if (municipioData && municipioData.lat && municipioData.lng) {
              map.panTo({ lat: municipioData.lat, lng: municipioData.lng });
              map.setZoom(13);
              setTimeout(() => setIsMapCentering(false), 450);
            } else {
              setIsMapCentering(false);
            }
          }
        } 
        // 2. Categoria Filtrada: Mostra todos os eventos da categoria
        else if (filterCategory && filterCategory.toLowerCase() !== 'todas') {
          const bounds = new LatLngBounds();
          let hasValidCoordinates = false;

          events.forEach(event => {
            if (event.lat && event.lng) {
              bounds.extend({ lat: parseFloat(event.lat), lng: parseFloat(event.lng) });
              hasValidCoordinates = true;
            }
          });

          if (hasValidCoordinates) {
            map.fitBounds(bounds, {
              top: 50,
              right: 50,
              bottom: 50,
              left: 50
            });
            const listener = map.addListener('idle', () => {
              if (map.getZoom() > 14) map.setZoom(14);
              window.google.maps.event.removeListener(listener);
              setIsMapCentering(false);
            });
          } else {
            setIsMapCentering(false);
          }
        } 
        // 3. Reset / Todos os Municípios: Visão Panorâmica Ampla dos 13 Municípios com Folga
        else {
          const regBounds = new LatLngBounds(
            { lat: REGIAO_BOUNDS.south, lng: REGIAO_BOUNDS.west },
            { lat: REGIAO_BOUNDS.north, lng: REGIAO_BOUNDS.east }
          );

          const isMobile = window.innerWidth < 768;
          const pad = isMobile ? 25 : 50;
          map.fitBounds(regBounds, {
            top: pad,
            right: pad,
            bottom: pad,
            left: pad
          });

          const idleListener = map.addListener('idle', () => {
            if (mapInstanceRef.current) {
              const z = mapInstanceRef.current.getZoom();
              if (z && onInitialZoomSetRef.current) {
                onInitialZoomSetRef.current(z);
              }
            }
            window.google.maps.event.removeListener(idleListener);
          });

          setTimeout(() => setIsMapCentering(false), 450);
        }
      } catch (error) {
        console.error("Erro ao ajustar limites do mapa:", error);
        setMapError("Erro ao processar a localização.");
        setIsMapCentering(false);
      }
    };

    adjustMapBounds();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdsString, filterMunicipio, filterCategory, mapLoaded]);

  // Dispara retorno suave ao zoom normal / inicial (visão dos 13 municípios)
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded || !resetViewTrigger) return;

    const resetMapToInitial = async () => {
      const map = mapInstanceRef.current;
      try {
        const { LatLngBounds } = await importLibrary("core");
        const regBounds = new LatLngBounds(
          { lat: REGIAO_BOUNDS.south, lng: REGIAO_BOUNDS.west },
          { lat: REGIAO_BOUNDS.north, lng: REGIAO_BOUNDS.east }
        );

        const isMobile = window.innerWidth < 768;
        const pad = isMobile ? 25 : 50;
        map.fitBounds(regBounds, {
          top: pad,
          right: pad,
          bottom: pad,
          left: pad
        });
      } catch (err) {
        console.error("Erro ao resetar zoom do mapa:", err);
      }
    };

    resetMapToInitial();
  }, [resetViewTrigger, mapLoaded]);

  // Check for out-of-bounds markers (Optimized with debounce)
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded || !setOutOfBoundsIndicators) return;

    const checkBounds = () => {
      const map = mapInstanceRef.current;
      const bounds = map.getBounds();
      if (!bounds) return;

      const mapDiv = mapRef.current;
      if (!mapDiv) return;

      const rect = mapDiv.getBoundingClientRect();
      const mapCenter = map.getCenter();
      
      const newIndicators = [];
      const processedEvents = new Set(); // Para evitar múltiplos indicadores para a mesma direção

      events.forEach(event => {
        if (!event.lat || !event.lng) return;
        
        const position = new window.google.maps.LatLng(parseFloat(event.lat), parseFloat(event.lng));
        
        if (!bounds.contains(position)) {
          // Calcula o ângulo entre o centro do mapa e o evento
          const dy = position.lat() - mapCenter.lat();
          const dx = position.lng() - mapCenter.lng();
          
          // Ajuste para o sistema de coordenadas da tela (y cresce para baixo)
          let angle = Math.atan2(-dy, dx);
          
          // Agrupar indicadores próximos (raio de ~15 graus)
          const angleKey = Math.round(angle * 12); // Quantiza o ângulo
          
          if (!processedEvents.has(angleKey)) {
            processedEvents.add(angleKey);
            
            // Calcula a posição na borda da tela
            // Considera um padding de 40px para o indicador não colar na borda
            const padding = 40;
            const hw = (rect.width / 2) - padding;
            const hh = (rect.height / 2) - padding;
            
            // Interseção da linha (centro -> evento) com o retângulo da tela
            let x, y;
            const tan = Math.tan(angle);
            
            // Verifica interseção com as bordas esquerda/direita
            if (Math.abs(tan) < hh / hw) {
              x = dx > 0 ? hw : -hw;
              y = x * tan;
            } 
            // Verifica interseção com as bordas superior/inferior
            else {
              y = -dy > 0 ? hh : -hh;
              x = y / tan;
            }

            // Converte para porcentagem da tela (0 a 100%) para uso no CSS
            const percentX = 50 + (x / (rect.width / 2)) * 50;
            const percentY = 50 + (y / (rect.height / 2)) * 50;

            // Converter ângulo para graus para rotação do ícone CSS
            const rotationDeg = (angle * 180) / Math.PI;

            newIndicators.push({
              id: `indicator-${angleKey}`,
              x: percentX,
              y: percentY,
              rotation: rotationDeg,
              targetLat: parseFloat(event.lat),
              targetLng: parseFloat(event.lng),
              eventId: event.id
            });
          }
        }
      });

      setOutOfBoundsIndicators(newIndicators);
    };

    const debouncedCheckBounds = () => {
      if (boundsTimeoutRef.current) {
        clearTimeout(boundsTimeoutRef.current);
      }
      boundsTimeoutRef.current = setTimeout(checkBounds, 50); // 50ms debounce for smoother updates
    };

    const map = mapInstanceRef.current;
    const idleListener = map.addListener('idle', debouncedCheckBounds);
    const boundsListener = map.addListener('bounds_changed', debouncedCheckBounds);
    const centerListener = map.addListener('center_changed', debouncedCheckBounds); // Importante para atualizar durante o arraste

    // Initial check
    checkBounds();

    return () => {
      if (boundsTimeoutRef.current) clearTimeout(boundsTimeoutRef.current);
      window.google.maps.event.removeListener(idleListener);
      window.google.maps.event.removeListener(boundsListener);
      window.google.maps.event.removeListener(centerListener);
    };
  }, [events, mapLoaded, setOutOfBoundsIndicators]);

  return (
    <div className="w-full h-full relative">
      <div ref={mapRef} className="w-full h-full" />
      
      {/* Indicador de carregamento/centralização */}
      {(isMapCentering || !mapLoaded) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] transition-all duration-300">
          <div className="flex flex-col items-center gap-3 bg-slate-800/90 p-6 rounded-2xl border border-cyan-500/30 shadow-2xl">
            <div className="w-10 h-10 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin" />
            <p className="text-cyan-400 text-sm font-medium animate-pulse">
              {mapLoaded ? 'Centralizando mapa...' : 'Carregando mapa...'}
            </p>
          </div>
        </div>
      )}

      {/* Mensagem de Erro */}
      {mapError && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-md">
          <div className="bg-red-900/90 backdrop-blur-md border border-red-500/50 p-4 rounded-xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4">
            <div className="bg-red-500/20 p-2 rounded-lg">
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-red-200 text-sm font-medium">{mapError}</p>
            <button 
              onClick={() => setMapError(null)}
              className="ml-auto text-red-400 hover:text-red-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapWithEvents;
